import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildTaskEditorBlockGroups,
  getTaskEditorBranchSignature,
  TaskEditorBlock,
  TaskEditorBlockGroup,
  TaskEditorState,
  UNCATEGORIZED_SET_NUMBER,
} from "pages/task-editor/utils/taskEditorUtils";

// header 블록은 단독으로 reveal 되지 않는다. 다음 body 블록까지 같이 보여야
// 의미가 통하므로, advance 후 header 거나 분기에서 제외된 블록은 건너뛰며
// 다음 노출 가능 위치로 이동.
function expandPastHeaders(
  upTo: number,
  blocks: TaskEditorBlock[],
  excludedSequences: ReadonlySet<number>
): number {
  let next = upTo;
  while (next < blocks.length) {
    const block = blocks[next - 1];
    if (!block) break;
    const isHeader = block.type === "header";
    const isExcluded = excludedSequences.has(next);
    if (!isHeader && !isExcluded) break;
    next += 1;
  }
  return Math.min(next, blocks.length);
}

// 분기 선택 결과 제외(스킵)되는 블록 sequence — 선택되지 않은(또는 아직 선택
// 전인) 옵션 세그먼트 전체. 상태로 저장하지 않고 selections 에서 파생한다.
function deriveExcludedSequences(
  groups: TaskEditorBlockGroup[],
  buttonSelections: Record<number, number[]>
): Set<number> {
  const excluded = new Set<number>();
  for (const group of groups) {
    if (group.kind !== "branch") continue;
    const selected = buttonSelections[group.index]?.[0];
    for (const segment of group.segments) {
      if (segment.optionIndex === selected) continue;
      for (let i = segment.start; i < segment.end; i += 1) {
        excluded.add(i + 1);
      }
    }
  }
  return excluded;
}

// ─── 타입 ─────────────────────────────────────────────────────────────────

export type PreviewSessionState = {
  revealedUpTo: number; // 보이는 마지막 블록 sequence (1-based). 정적 모드면 task.blocks.length
  textInputs: Record<number, Record<number, string>>; // [blockIdx][fieldIdx] = value
  buttonSelections: Record<number, number[]>; // [blockIdx] = 선택된 옵션 indexes
  quizState: Record<number, { tried: number[]; correct: boolean }>;
};

export type PreviewSessionApi = {
  state: PreviewSessionState;
  // 분기 선택으로 제외된 sequence (파생값) — 렌더는 이 set 의 블록을 숨긴다.
  excludedSequences: ReadonlySet<number>;
  reset: () => void;
  setTextInput: (blockIdx: number, fieldIdx: number, value: string) => void;
  toggleButtonDefault: (blockIdx: number, optionIdx: number) => void;
  attemptQuiz: (blockIdx: number, optionIdx: number) => void;
  chooseBranch: (blockIdx: number, optionIdx: number) => void;
  advanceFrom: (blockIdx: number) => void;
  isBlockComplete: (blockIdx: number) => boolean;
};

// ─── 초기 상태 생성 ───────────────────────────────────────────────────────

function makeInitialState(
  task: TaskEditorState,
  viewerMode: "patient" | "coach",
  isTrialEnabled: boolean
): PreviewSessionState {
  // 체험 ON + patient 탭에서만 첫 블록부터 reveal. 첫 블록이 header 라면
  // 다음 body 까지 같이 reveal.
  const initialUpTo =
    isTrialEnabled && viewerMode === "patient" ? 1 : task.blocks.length;
  const revealedUpTo =
    isTrialEnabled && viewerMode === "patient"
      ? expandPastHeaders(
          initialUpTo,
          task.blocks,
          deriveExcludedSequences(buildTaskEditorBlockGroups(task.blocks), {})
        )
      : initialUpTo;
  return {
    revealedUpTo,
    textInputs: {},
    buttonSelections: {},
    quizState: {},
  };
}

// ─── hook ────────────────────────────────────────────────────────────────

function usePreviewSession(
  task: TaskEditorState | undefined,
  opts: { viewerMode: "patient" | "coach"; isTrialEnabled: boolean }
): PreviewSessionApi {
  const { viewerMode, isTrialEnabled } = opts;

  // task 가 undefined 일 때도 hook 을 안전하게 호출하기 위해 fallback 사용
  const safeTask: TaskEditorState = useMemo(
    () =>
      task ?? {
        task_id: "",
        title: "",
        set_number: UNCATEGORIZED_SET_NUMBER,
        blocks: [],
      },
    [task]
  );

  const groups = useMemo(
    () => buildTaskEditorBlockGroups(safeTask.blocks),
    [safeTask.blocks]
  );

  const [sessionState, setSessionState] = useState<PreviewSessionState>(() =>
    makeInitialState(safeTask, viewerMode, isTrialEnabled)
  );

  const excludedSequences = useMemo(
    () => deriveExcludedSequences(groups, sessionState.buttonSelections),
    [groups, sessionState.buttonSelections]
  );

  // reset 트리거 조건: task_id, 분기 구조 시그니처(블록 수·분기/태그 배치),
  // viewerMode, isTrialEnabled
  const branchSignature = getTaskEditorBranchSignature(safeTask.blocks);
  const prevTaskIdRef = useRef(safeTask.task_id);
  const prevBranchSignatureRef = useRef(branchSignature);
  const prevViewerModeRef = useRef(viewerMode);
  const prevIsTrialEnabledRef = useRef(isTrialEnabled);

  useEffect(() => {
    const taskIdChanged = prevTaskIdRef.current !== safeTask.task_id;
    const branchSignatureChanged =
      prevBranchSignatureRef.current !== branchSignature;
    const viewerModeChanged = prevViewerModeRef.current !== viewerMode;
    const isTrialEnabledChanged =
      prevIsTrialEnabledRef.current !== isTrialEnabled;

    prevTaskIdRef.current = safeTask.task_id;
    prevBranchSignatureRef.current = branchSignature;
    prevViewerModeRef.current = viewerMode;
    prevIsTrialEnabledRef.current = isTrialEnabled;

    if (
      taskIdChanged ||
      branchSignatureChanged ||
      viewerModeChanged ||
      isTrialEnabledChanged
    ) {
      setSessionState(makeInitialState(safeTask, viewerMode, isTrialEnabled));
    }
  }, [safeTask, branchSignature, viewerMode, isTrialEnabled]);

  const reset = useCallback(() => {
    setSessionState(makeInitialState(safeTask, viewerMode, isTrialEnabled));
  }, [safeTask, viewerMode, isTrialEnabled]);

  const setTextInput = useCallback(
    (blockIdx: number, fieldIdx: number, value: string) => {
      setSessionState((prev) => ({
        ...prev,
        textInputs: {
          ...prev.textInputs,
          [blockIdx]: {
            ...(prev.textInputs[blockIdx] ?? {}),
            [fieldIdx]: value,
          },
        },
      }));
    },
    []
  );

  const toggleButtonDefault = useCallback(
    (blockIdx: number, optionIdx: number) => {
      setSessionState((prev) => {
        const block = safeTask.blocks[blockIdx];
        if (!block?.action || block.action.type !== "button_default") {
          return prev;
        }
        const allowMultiple = block.action.allow_multiple_selection;
        const current = prev.buttonSelections[blockIdx] ?? [];
        let next: number[];
        if (allowMultiple) {
          next = current.includes(optionIdx)
            ? current.filter((i) => i !== optionIdx)
            : [...current, optionIdx];
        } else {
          next = current.includes(optionIdx) ? [] : [optionIdx];
        }
        return {
          ...prev,
          buttonSelections: { ...prev.buttonSelections, [blockIdx]: next },
        };
      });
    },
    [safeTask.blocks]
  );

  const attemptQuiz = useCallback(
    (blockIdx: number, optionIdx: number) => {
      setSessionState((prev) => {
        const block = safeTask.blocks[blockIdx];
        if (!block?.action || block.action.type !== "button_quiz") {
          return prev;
        }
        const option = block.action.options[optionIdx];
        const isCorrect = option?.is_correct === true;
        const prevQuiz = prev.quizState[blockIdx] ?? {
          tried: [],
          correct: false,
        };

        // v1 ButtonGroupCell 처럼 같은 옵션 다시 누르면 no-op. 다른 옵션을
        // 누르면 그 옵션을 last 로 옮긴다. correct 는 매 클릭마다 그 클릭의
        // 정/오답으로 갱신 — 정답 후 다른 오답 눌러도 alert 다시 표시.
        if (prevQuiz.tried[prevQuiz.tried.length - 1] === optionIdx) {
          return prev;
        }
        const newTried = [
          ...prevQuiz.tried.filter((i) => i !== optionIdx),
          optionIdx,
        ];
        const newQuizState = {
          ...prev.quizState,
          [blockIdx]: { tried: newTried, correct: isCorrect },
        };

        // advance 는 정답을 *처음* 맞춘 순간에만 1회. revealedUpTo 가 이미
        // 진행됐으면(이전에 정답 한 번 맞춘 후) 조건 안 맞아 재진행 X.
        const blockSeq = blockIdx + 1; // 1-based
        let newRevealedUpTo = prev.revealedUpTo;
        if (isCorrect && blockSeq === prev.revealedUpTo) {
          newRevealedUpTo = expandPastHeaders(
            prev.revealedUpTo + 1,
            safeTask.blocks,
            deriveExcludedSequences(groups, prev.buttonSelections)
          );
        }

        return {
          ...prev,
          revealedUpTo: newRevealedUpTo,
          quizState: newQuizState,
        };
      });
    },
    [safeTask.blocks, groups]
  );

  const chooseBranch = useCallback(
    (blockIdx: number, optionIdx: number) => {
      setSessionState((prev) => {
        const block = safeTask.blocks[blockIdx];
        if (!block?.action || block.action.type !== "button_branch") {
          return prev;
        }
        if (!block.action.options[optionIdx]) return prev;
        // 같은 옵션 재클릭은 no-op — 진행/입력 상태 보존.
        if ((prev.buttonSelections[blockIdx] ?? [])[0] === optionIdx) {
          return prev;
        }

        const group = groups.find(
          (g) => g.kind === "branch" && g.index === blockIdx
        );
        const segments = group?.kind === "branch" ? group.segments : [];

        // 선택 갱신 + 다른 옵션 세그먼트의 입력 상태 제거 (선택 변경 시 리셋)
        const nextSelections = {
          ...prev.buttonSelections,
          [blockIdx]: [optionIdx],
        };
        const nextTextInputs = { ...prev.textInputs };
        const nextQuizState = { ...prev.quizState };
        for (const segment of segments) {
          if (segment.optionIndex === optionIdx) continue;
          for (let i = segment.start; i < segment.end; i += 1) {
            delete nextTextInputs[i];
            delete nextSelections[i];
            delete nextQuizState[i];
          }
        }

        // 노출 커서를 분기 직후로 — 선택 세그먼트 첫 블록(빈 세그먼트면
        // 합류점)으로 확장. 선택 변경이면 합류 이후를 지났더라도 되감는다.
        const nextExcluded = deriveExcludedSequences(groups, nextSelections);
        const revealedUpTo = expandPastHeaders(
          blockIdx + 2,
          safeTask.blocks,
          nextExcluded
        );

        return {
          ...prev,
          revealedUpTo,
          buttonSelections: nextSelections,
          textInputs: nextTextInputs,
          quizState: nextQuizState,
        };
      });
    },
    [safeTask.blocks, groups]
  );

  const advanceFrom = useCallback(
    (blockIdx: number) => {
      setSessionState((prev) => {
        const blockSeq = blockIdx + 1; // 1-based
        if (blockSeq !== prev.revealedUpTo) return prev;

        const next = expandPastHeaders(
          prev.revealedUpTo + 1,
          safeTask.blocks,
          deriveExcludedSequences(groups, prev.buttonSelections)
        );
        return { ...prev, revealedUpTo: next };
      });
    },
    [safeTask.blocks, groups]
  );

  const isBlockComplete = useCallback(
    (blockIdx: number): boolean => {
      const block = safeTask.blocks[blockIdx];
      if (!block) return false;
      const action = block.action;
      if (!action) return true;

      switch (action.type) {
        case "text_input": {
          // v1 logics.ts 와 동일 — optional 필드는 검증 제외, 필수 필드는
          // 비어있지 않아야 진행 (기본 필수).
          const values = sessionState.textInputs[blockIdx] ?? {};
          return action.fields.every(
            (field, fieldIdx) =>
              field.optional === true ||
              (values[fieldIdx] ?? "").trim().length > 0
          );
        }
        case "button_default": {
          const selections = sessionState.buttonSelections[blockIdx] ?? [];
          return selections.length >= 1;
        }
        case "button_quiz": {
          const quiz = sessionState.quizState[blockIdx];
          return quiz?.correct === true;
        }
        case "button_branch": {
          const selections = sessionState.buttonSelections[blockIdx] ?? [];
          return selections.length >= 1;
        }
        case "audio": {
          // 오디오는 끝까지 재생을 강제하지 않고 언제든 다음 블록으로 진행 가능.
          return true;
        }
        default: {
          // exhaustiveness check
          const _never: never = action;
          return _never;
        }
      }
    },
    [
      safeTask.blocks,
      sessionState.buttonSelections,
      sessionState.quizState,
      sessionState.textInputs,
    ]
  );

  return useMemo(
    () => ({
      state: sessionState,
      excludedSequences,
      reset,
      setTextInput,
      toggleButtonDefault,
      attemptQuiz,
      chooseBranch,
      advanceFrom,
      isBlockComplete,
    }),
    [
      sessionState,
      excludedSequences,
      reset,
      setTextInput,
      toggleButtonDefault,
      attemptQuiz,
      chooseBranch,
      advanceFrom,
      isBlockComplete,
    ]
  );
}

export default usePreviewSession;
